import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Select } from 'common/components/antd/Select/Select';
import { Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { isValidNumberInRange } from 'common/utils/form.utils';
import { notifySuccess } from 'common/utils/notifier.utils';
import {
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
} from 'domains/permissions/queries/permissions.query';
import {
  TCreatePermissionReqData,
  TUpdatePermissionReqData,
} from 'domains/permissions/types/permissions.types';
import { SFormContainer, SFooter } from './modal.style';
import { EPermissionsCreateEditForm, EModalType } from '../enums/permissions.enums';
import { TPermissionModalData } from '../types/permissions.types';

type TProps = {
	isOpen: boolean;
	onCancelModalHandler: () => void;
	data: TPermissionModalData;
};

const CreateEditPermissionsModal = (props: TProps): ReactElement => {
	const {
		isOpen,
		onCancelModalHandler,
		data: { rowData, type: modalType},
	} = props;
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);
	const { mutateAsync: createPermissionMutation } = useCreatePermissionMutation();
	const { mutateAsync: updatePermissionMutation } = useUpdatePermissionMutation();

  const ketoKindOptions = [
    {
      label: 'endpoint',
      value: t('usrRoleMng.permissions.modal.field.ketoKind.options.endpoint'),
    },
    {
      label: 'branch',
      value: t('usrRoleMng.permissions.modal.field.ketoKind.options.branch'),
    },
  ];

  const ketoPermissionNameOptions = [
    {
      label: 'GET',
      value: 'GET',
    },
    {
      label: 'POST',
      value: 'POST',
    },
    {
      label: 'PUT',
      value: 'PUT',
    },
    {
      label: 'DELETE',
      value: 'DELETE',
    },
    {
      label: 'PATCH',
      value: 'PATCH',
    },
  ];

	useEffect(() => {
		if (Object.keys(rowData || {}).length) {
			const {
        guid,
        default_group_name,
        default_name,
        keto_permission_name,
        keto_scope_name,
        keto_kind,
        group_sort_number,
        name_sort_number,
			} = rowData;

			form.setFields([
        {
          name: EPermissionsCreateEditForm.GUID,
          value: guid,
        },
				{
					name: EPermissionsCreateEditForm.GroupName,
					value: default_group_name,
				},
				{
					name: EPermissionsCreateEditForm.Name,
					value: default_name,
				},
				{
					name: EPermissionsCreateEditForm.KetoPermissionName,
					value: keto_permission_name,
				},
				{
					name: EPermissionsCreateEditForm.KetoScopeName,
					value: keto_scope_name,
				},
        {
          name: EPermissionsCreateEditForm.KetoKind,
          value: keto_kind,
        },
        {
          name: EPermissionsCreateEditForm.GroupSortNumber,
          value: group_sort_number.toString(),
        },
        {
          name: EPermissionsCreateEditForm.NameSortNumber,
          value: name_sort_number.toString(),
        },
			]);
		}
	}, [rowData]);

	const onCloseModalHandler = (): void => {
		form.resetFields();
		onCancelModalHandler();
	};

	const onFinishFormHandler = async (formData: Record<EPermissionsCreateEditForm, string>): Promise<void> => {
		try {
			setIsSubmitBtnLoading(true);

			const {
        default_group_name,
        default_name,
        group_lang_key = 'string',
        group_sort_number,
        guid,
        keto_kind,
        keto_permission_name,
        keto_scope_name,
        name_lang_key = 'string',
        name_sort_number,
			} = formData;

			if (modalType === EModalType.Edit) {
				const updatePermissionReqData: TUpdatePermissionReqData = {
          default_group_name,
          default_name,
          group_lang_key,
          group_sort_number: Number(group_sort_number),
          guid,
          keto_kind,
          keto_permission_name,
          keto_scope_name,
          name_lang_key,
          name_sort_number: Number(name_sort_number),
				};

				await updatePermissionMutation({
					permissionGUID: form.getFieldValue([EPermissionsCreateEditForm.GUID]),
					requestData: updatePermissionReqData,
				});
				notifySuccess('usrRoleMng.permissions.modal.edit.notification.success');
			} else {
				const createPermissionReqData: TCreatePermissionReqData = {
          default_group_name,
          default_name,
          group_lang_key,
          group_sort_number: Number(group_sort_number),
          keto_kind,
          keto_permission_name,
          keto_scope_name,
          name_lang_key,
          name_sort_number: Number(name_sort_number),
				};
				await createPermissionMutation({
					requestData: createPermissionReqData,
				});
				notifySuccess('usrRoleMng.permissions.modal.create.notification.success');
			}

			await queryClient.invalidateQueries({
				queryKey: queryKeysClient[EQueryKeys.PermissionQueryKeys].getPermissionsList._def,
			});

			setIsSubmitBtnLoading(false);
			onCancelModalHandler();
		} catch (error) {
			console.error(error);
			setIsSubmitBtnLoading(false);
		}
	};

	return (
		<EnhancedModal width={764} open={isOpen} onCancel={onCloseModalHandler}>
			<Form
        form={form}
        onFinish={onFinishFormHandler}
        initialValues={{
          [EPermissionsCreateEditForm.GroupSortNumber]: '0',
          [EPermissionsCreateEditForm.NameSortNumber]: '0',
        }}
      >
				<Header>
					{modalType === EModalType.Edit
						? t('usrRoleMng.permissions.modal.edit.title')
						: t('usrRoleMng.permissions.modal.create.title')}
				</Header>
				<SFormContainer>
          <Form.Item
            name={EPermissionsCreateEditForm.GroupName}
            label={t('usrRoleMng.permissions.modal.field.defaultGroupName.label')}
          >
            <Input
              placeholder={t('usrRoleMng.permissions.modal.field.defaultGroupName.placeholder') as string}
              size="large"
            />
          </Form.Item>
					<Form.Item
						name={EPermissionsCreateEditForm.Name}
						label={t('usrRoleMng.permissions.modal.field.name.label')}
					>
						<Input
							placeholder={t('usrRoleMng.permissions.modal.field.name.placeholder') as string}
							size="large"
						/>
					</Form.Item>

          <Form.Item
            name={EPermissionsCreateEditForm.KetoKind}
            label={t('usrRoleMng.permissions.modal.field.ketoKind.label')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              placeholder={t('usrRoleMng.permissions.modal.field.ketoKind.placeholder')}
            >
              {(ketoKindOptions || []).map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={EPermissionsCreateEditForm.KetoPermissionName}
            label={t('usrRoleMng.permissions.modal.field.ketoPermissionName.label')}
          >
            <Select
              style={{ width: '100%' }}
              size="large"
              placeholder={t('usrRoleMng.permissions.modal.field.ketoPermissionName.placeholder')}
            >
              {(ketoPermissionNameOptions || []).map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name={EPermissionsCreateEditForm.GroupSortNumber}
            label={t('usrRoleMng.permissions.modal.field.groupSortNumber.label')}
            rules={[
              {
                message: t('usrRoleMng.permissions.modal.field.groupSortNumber.validator.message') as string,
                validator: (_, value: string): Promise<any> => {
                  if (!isValidNumberInRange(value)) {
                    return Promise.reject();
                  }

                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              placeholder={
                t('usrRoleMng.permissions.modal.field.groupSortNumber.placeholder') as string
              }
              size="large"
            />
          </Form.Item>
          <Form.Item
            name={EPermissionsCreateEditForm.KetoScopeName}
            label={t('usrRoleMng.permissions.modal.field.ketoScopeName.label')}
          >
            <Input
              placeholder={t('usrRoleMng.permissions.modal.field.ketoScopeName.placeholder') as string}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name={EPermissionsCreateEditForm.NameSortNumber}
            label={t('usrRoleMng.permissions.modal.field.nameSortNumber.label')}
            rules={[
              {
                message: t('usrRoleMng.permissions.modal.field.nameSortNumber.validator.message') as string,
                validator: (_, value: string): Promise<any> => {
                  if (!isValidNumberInRange(value)) {
                    return Promise.reject();
                  }

                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              placeholder={t('usrRoleMng.permissions.modal.field.nameSortNumber.placeholder') as string}
              size="large"
            />
          </Form.Item>
				</SFormContainer>
				<Footer>
					<SFooter>
						<Button style={{ marginRight: 12 }} onClick={onCloseModalHandler}>
							{t('usrRoleMng.permissions.modal.button.cancel')}
						</Button>
						<Button loading={isSubmitBtnLoading} htmlType="submit" type="primary">
							{modalType === EModalType.Edit
								? t('usrRoleMng.permissions.modal.button.edit')
								: t('usrRoleMng.permissions.modal.button.create')}
						</Button>
					</SFooter>
				</Footer>
			</Form>
		</EnhancedModal>
	);
};

export default CreateEditPermissionsModal;
